// @ts-nocheck
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Edit, Trash2, Copy, Calendar } from 'lucide-react';
import { useAffiliateCoupons } from '@/hooks/useAffiliateCoupons';
import { CreateCouponRequest } from '@/types/affiliateCoupons';
import { toast } from 'sonner';

interface CouponFormData {
  affiliate_id: string;
  code: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: string;
  usage_limit: string;
  valid_from: string;
  valid_until: string;
  is_active: boolean;
}

const AffiliateCouponCodesTab = () => {
  const { coupons, loading, createCoupon, updateCoupon, deleteCoupon } = useAffiliateCoupons();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedAffiliateId, setSelectedAffiliateId] = useState('');
  
  const [formData, setFormData] = useState<CouponFormData>({
    affiliate_id: '',
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    usage_limit: '',
    valid_from: new Date().toISOString().split('T')[0],
    valid_until: '',
    is_active: true
  });

  const generateCouponCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, code: result });
  };

  const handleCreateCoupon = async () => {
    if (!formData.affiliate_id || !formData.code || !formData.discount_value) {
      toast.error('Please fill in all required fields');
      return;
    }

    const couponData: CreateCouponRequest = {
      affiliate_id: formData.affiliate_id,
      code: formData.code.toUpperCase(),
      discount_type: formData.discount_type,
      discount_value: parseFloat(formData.discount_value),
      usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
      valid_from: formData.valid_from,
      valid_until: formData.valid_until || null,
      is_active: formData.is_active
    };

    const success = await createCoupon(couponData);
    if (success) {
      setIsCreateDialogOpen(false);
      setFormData({
        affiliate_id: '',
        code: '',
        discount_type: 'percentage',
        discount_value: '',
        usage_limit: '',
        valid_from: new Date().toISOString().split('T')[0],
        valid_until: '',
        is_active: true
      });
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success('Coupon code copied to clipboard');
  };

  const getStatusBadge = (coupon: any) => {
    if (!coupon.is_active) {
      return <Badge variant="secondary">Inactive</Badge>;
    }
    
    const now = new Date();
    const validUntil = coupon.valid_until ? new Date(coupon.valid_until) : null;
    
    if (validUntil && validUntil < now) {
      return <Badge variant="destructive">Expired</Badge>;
    }
    
    if (coupon.usage_limit && coupon.usage_count >= coupon.usage_limit) {
      return <Badge variant="outline">Limit Reached</Badge>;
    }
    
    return <Badge variant="default">Active</Badge>;
  };

  const formatDiscountValue = (type: string, value: number) => {
    return type === 'percentage' ? `${value}%` : `$${value.toFixed(2)}`;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Loading coupon codes...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Affiliate Coupon Codes</CardTitle>
              <CardDescription>
                Manage discount codes that affiliates can share with their audience
              </CardDescription>
            </div>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Coupon
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Create Affiliate Coupon Code</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="affiliate_id">Affiliate ID</Label>
                    <Input
                      id="affiliate_id"
                      value={formData.affiliate_id}
                      onChange={(e) => setFormData({ ...formData, affiliate_id: e.target.value })}
                      placeholder="Enter affiliate ID"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="code">Coupon Code</Label>
                    <div className="flex gap-2">
                      <Input
                        id="code"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                        placeholder="Enter code or generate"
                      />
                      <Button variant="outline" onClick={generateCouponCode}>
                        Generate
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="discount_type">Discount Type</Label>
                    <Select 
                      value={formData.discount_type} 
                      onValueChange={(value: 'percentage' | 'fixed') => 
                        setFormData({ ...formData, discount_type: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage</SelectItem>
                        <SelectItem value="fixed">Fixed Amount</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="discount_value">
                      Discount Value {formData.discount_type === 'percentage' ? '(%)' : '($)'}
                    </Label>
                    <Input
                      id="discount_value"
                      type="number"
                      value={formData.discount_value}
                      onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                      placeholder={formData.discount_type === 'percentage' ? '10' : '5.00'}
                    />
                  </div>

                  <div>
                    <Label htmlFor="usage_limit">Usage Limit (Optional)</Label>
                    <Input
                      id="usage_limit"
                      type="number"
                      value={formData.usage_limit}
                      onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })}
                      placeholder="Leave empty for unlimited"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="valid_from">Valid From</Label>
                      <Input
                        id="valid_from"
                        type="date"
                        value={formData.valid_from}
                        onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="valid_until">Valid Until (Optional)</Label>
                      <Input
                        id="valid_until"
                        type="date"
                        value={formData.valid_until}
                        onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreateCoupon}>
                      Create Coupon
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {coupons.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">No coupon codes found</p>
              <p className="text-sm text-gray-400">Create your first affiliate coupon code to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Discount</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {coupons.map((coupon) => (
                  <TableRow key={coupon.id}>
                    <TableCell className="font-mono font-medium">
                      <div className="flex items-center gap-2">
                        {coupon.code}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyCode(coupon.code)}
                          className="h-6 w-6 p-0"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      {formatDiscountValue(coupon.discount_type, coupon.discount_value)}
                    </TableCell>
                    <TableCell>
                      {coupon.usage_count}
                      {coupon.usage_limit && ` / ${coupon.usage_limit}`}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(coupon)}
                    </TableCell>
                    <TableCell>
                      {coupon.valid_until ? (
                        <div className="flex items-center gap-1 text-sm">
                          <Calendar className="h-3 w-3" />
                          {new Date(coupon.valid_until).toLocaleDateString()}
                        </div>
                      ) : (
                        <span className="text-gray-500 text-sm">No expiry</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm">
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => deleteCoupon(coupon.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AffiliateCouponCodesTab;